import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Edit2, Check, X, AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { bookingsAPI } from '../../services/api';
import { toast } from 'sonner';

/**
 * TimeOverrideControl Component
 * Allows Owner to view and override time estimates for bookings
 */
const TimeOverrideControl = ({ 
  booking,
  onUpdate,
  compact = false 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [hours, setHours] = useState(Math.floor((booking?.owner_override_minutes || booking?.estimated_total_minutes || 60) / 60));
  const [minutes, setMinutes] = useState((booking?.owner_override_minutes || booking?.estimated_total_minutes || 60) % 60);
  const [saving, setSaving] = useState(false);

  const estimatedMinutes = booking?.estimated_total_minutes || 0;
  const actualMinutes = booking?.actual_labor_minutes || 0;
  const overrideMinutes = booking?.owner_override_minutes;
  const varianceMinutes = booking?.time_variance_minutes;
  
  const officialEstimate = overrideMinutes || estimatedMinutes;

  const handleSave = async () => {
    const totalMinutes = (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
    
    if (totalMinutes < 15) {
      toast.error('Minimum estimate is 15 minutes');
      return;
    }
    
    setSaving(true);
    try {
      await bookingsAPI.overrideTime(booking.id, { override_minutes: totalMinutes });
      toast.success('Time estimate updated');
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update time estimate');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setHours(Math.floor(officialEstimate / 60));
    setMinutes(officialEstimate % 60);
    setIsEditing(false);
  };

  const formatTime = (mins) => {
    if (!mins) return 'Not set';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} hr${h > 1 ? 's' : ''}`;
    return `${h}h ${m}m`;
  };

  const getVarianceIcon = () => {
    if (!varianceMinutes) return null;
    if (varianceMinutes > 15) return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (varianceMinutes < -15) return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <Clock className="w-4 h-4 text-[var(--primary)]" />
        <span className="text-[var(--text-secondary)]">Est:</span>
        <span className="font-medium">{formatTime(officialEstimate)}</span>
        {overrideMinutes && (
          <span className="text-xs text-[var(--primary)]">(Override)</span>
        )}
        {actualMinutes > 0 && (
          <>
            <span className="text-[var(--text-muted)]">|</span>
            <span className="text-[var(--text-secondary)]">Actual:</span>
            <span className="font-medium">{formatTime(actualMinutes)}</span>
            {getVarianceIcon()}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4" data-testid="time-override-control">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold flex items-center gap-2">
          <Clock className="w-5 h-5 text-[var(--primary)]" />
          Time Estimate
        </h4>
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
            className="gap-1"
            data-testid="edit-time-btn"
          >
            <Edit2 className="w-3.5 h-3.5" />
            Override
          </Button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {isEditing ? (
          <motion.div
            key="editing"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-3"
          >
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label className="text-xs text-[var(--text-muted)]">Hours</Label>
                <Input
                  type="number"
                  min="0"
                  max="24"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  className="mt-1"
                  data-testid="time-hours-input"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-[var(--text-muted)]">Minutes</Label>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  step="5"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  className="mt-1"
                  data-testid="time-minutes-input"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-[var(--primary)] hover:bg-[var(--primary-dark)] text-black"
                data-testid="save-time-btn"
              >
                {saving ? 'Saving...' : <><Check className="w-4 h-4 mr-1" /> Save</>}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancel}
                className="flex-1"
              >
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="display"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-2"
          >
            {/* System Estimate */}
            <div className="flex justify-between items-center py-2 border-b border-[var(--border-primary)]">
              <span className="text-[var(--text-secondary)]">System Estimate:</span>
              <span className="font-medium">{formatTime(estimatedMinutes)}</span>
            </div>
            
            {/* Owner Override */}
            {overrideMinutes && (
              <div className="flex justify-between items-center py-2 border-b border-[var(--border-primary)]">
                <span className="text-[var(--text-secondary)]">Owner Override:</span>
                <span className="font-bold text-[var(--primary)]">{formatTime(overrideMinutes)}</span>
              </div>
            )}
            
            {/* Official Estimate */}
            <div className="flex justify-between items-center py-2 bg-[var(--primary)]/10 rounded-lg px-3">
              <span className="font-medium">Official Estimate:</span>
              <span className="font-bold text-lg text-[var(--primary)]">{formatTime(officialEstimate)}</span>
            </div>
            
            {/* Actual Time (if completed) */}
            {actualMinutes > 0 && (
              <>
                <div className="flex justify-between items-center py-2 border-b border-[var(--border-primary)]">
                  <span className="text-[var(--text-secondary)]">Actual Time:</span>
                  <span className="font-medium">{formatTime(actualMinutes)}</span>
                </div>
                
                {/* Variance */}
                <div className={`flex justify-between items-center py-2 px-3 rounded-lg ${
                  varianceMinutes > 15 ? 'bg-red-500/10' : 
                  varianceMinutes < -15 ? 'bg-green-500/10' : 
                  'bg-gray-100'
                }`}>
                  <span className="flex items-center gap-2">
                    {getVarianceIcon()}
                    <span className="text-[var(--text-secondary)]">Variance:</span>
                  </span>
                  <span className={`font-bold ${
                    varianceMinutes > 15 ? 'text-red-500' : 
                    varianceMinutes < -15 ? 'text-green-500' : 
                    ''
                  }`}>
                    {varianceMinutes > 0 ? '+' : ''}{varianceMinutes} min
                  </span>
                </div>
              </>
            )}

            {/* Note about override */}
            {overrideMinutes && booking?.owner_override_at && (
              <p className="text-xs text-[var(--text-muted)] mt-2">
                Override set on {new Date(booking.owner_override_at).toLocaleDateString()}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TimeOverrideControl;
